import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { formatINR } from '@/lib/format';
import { dmy, getVoucher, listVouchers, type NoteKind, type VoucherRow } from '@/api/accountsBooks';
import { VoucherFormModal } from './VoucherFormModal';
import { VoucherView } from './VoucherRegisterPage';

/** Credit & Debit Notes, as the prototype: tabs for Credit Notes, Rate Difference and Debit Notes. */
const TABS: [NoteKind, string, string][] = [['cn', 'Credit Notes', 'New Credit Note'], ['rd', 'Rate Difference', 'New Rate Difference Note'], ['dn', 'Debit Notes', 'New Debit Note']];

export function NotesPage() {
  const period = usePeriod('month');
  const [tab, setTab] = useState<NoteKind>('cn');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<{ id: number | null } | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const range = period.range;
  const type = tab === 'dn' ? 'Debit Note' : 'Credit Note';
  const credit = tab !== 'dn';

  const q = useQuery({
    queryKey: ['acc-books', 'vouchers', type, range?.from ?? '', range?.to ?? ''],
    queryFn: () => listVouchers(type, { from: range?.from, to: range?.to }),
  });
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (q.data ?? []).filter((r) => (r.details?.kind ?? (tab === 'dn' ? 'dn' : 'cn')) === tab)
      .filter((r) => !s || [r.voucherNo, r.narration, r.details?.party, r.details?.againstBill, r.details?.reason].some((x) => (x ?? '').toLowerCase().includes(s)));
  }, [q.data, search, tab]);
  const total = rows.filter((r) => r.status === 'Active').reduce((s, r) => s + (r.details?.total ?? r.amount), 0);
  const editQ = useQuery({ queryKey: ['acc-books', 'voucher', form?.id], queryFn: () => getVoucher(form!.id!), enabled: !!form?.id });
  const dash = (r: VoucherRow, v: React.ReactNode) => (r.status === 'Cancelled' || !r.details ? '—' : v);

  return (
    <div>
      <SectionHeader title="Credit & Debit Notes" />
      <div className="mb-2 inline-flex overflow-hidden rounded-lg border border-[var(--border-color)] text-xs font-bold">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-1.5 ${tab === k ? 'bg-accent/15 text-accent' : 'text-[var(--text-secondary)] hover:text-accent'}`}>{label}</button>
        ))}
      </div>
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" title="Note no., party, invoice / bill or reason" className="!pl-5 sm:!pl-8" />
          </div>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <Button variant="primary" size="sm" className="col-span-3 sm:col-span-1 sm:ml-auto sm:self-end" icon={<Plus size={12} />} onClick={() => setForm({ id: null })}>
          {TABS.find(([k]) => k === tab)![2]}
        </Button>
      </FilterBar>

      <Table<VoucherRow>
        storageKey={`acc-notes-${tab}`}
        title={TABS.find(([k]) => k === tab)![1]}
        count={`${rows.length} note${rows.length === 1 ? '' : 's'} · ₹${formatINR(total)}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isFetching && !q.data}
        empty="No notes in this period."
        columns={[
          { key: 'date', header: 'Date', cell: (r) => dmy(r.date) },
          { key: 'no', header: 'Note No', cell: (r) => <button type="button" onClick={() => setViewId(r.id)} className="font-mono font-semibold text-accent hover:underline">{r.voucherNo}</button> },
          { key: 'status', header: 'Status', cell: (r) => (r.status === 'Cancelled' ? <Badge tone="danger">Cancelled</Badge> : '—') },
          { key: 'party', header: credit ? 'Customer' : 'Supplier', minWidth: '180px', cell: (r) => dash(r, r.details?.party) },
          { key: 'against', header: credit ? 'Against Invoice' : 'Against Bill', cell: (r) => dash(r, <span className="font-mono">{r.details?.againstBill}</span>) },
          { key: 'reason', header: 'Reason', cell: (r) => dash(r, r.details?.reason) },
          { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => dash(r, `₹${formatINR(r.details?.taxable ?? 0)}`) },
          { key: 'gst', header: 'GST', align: 'right', cell: (r) => dash(r, `₹${formatINR(r.details ? r.details.cgst + r.details.sgst + r.details.igst : 0)}`) },
          { key: 'total', header: 'Total', align: 'right', cell: (r) => <strong>₹{formatINR(r.details?.total ?? r.amount)}</strong> },
        ]}
      />

      {viewId != null && <VoucherView id={viewId} onClose={() => setViewId(null)} onAlter={() => { setForm({ id: viewId }); setViewId(null); }} />}
      {form && (!form.id || editQ.data) && (
        <VoucherFormModal type={type} kind={tab} voucher={form.id ? editQ.data! : null} onClose={() => setForm(null)} />
      )}
    </div>
  );
}
